import type { Part } from '@athena-lib/SvgPreview/types';
import { type LoadedFont } from './types';

export function centerSVGChildElement(parent: SVGSVGElement, childElement: SVGElement) {
  const viewBox = parent.viewBox.baseVal;
  const viewBoxWidth = viewBox.width;
  const viewBoxHeight = viewBox.height;
  const scale = childElement.getAttribute('scale');

  // Get the bounding box of the child element
  const childBBox = (childElement as SVGGElement).getBBox();
  // Calculate the translation to center the child element within the viewBox
  const translateX = (viewBoxWidth - childBBox.width) / 2 - childBBox.x;
  const translateY = (viewBoxHeight - childBBox.height) / 2 - childBBox.y;

  // Apply the transform to the child element
  let transform = 'translate(' + translateX + ',' + translateY + ')';
  if (scale) {
    transform += ' scale(' + scale + ')';
  }
  childElement.setAttribute('transform', transform);

  /* returning value for ref elements */
  return transform;
}

export function centerSVGChildElementAndCounteractAnimationsTransform(
  parent: SVGSVGElement,
  childElement: SVGElement,
) {
  let inverseTransform = ''; // This will store the counter-transform

  if (childElement instanceof SVGUseElement) {
    inverseTransform = getInverseTransform(childElement);
  }

  const transform = getCenteringTransform(parent, childElement, inverseTransform);
  childElement.setAttribute('transform', transform);

  return transform;
}

function getInverseTransform(childElement: SVGUseElement): string {
  const href = childElement.getAttribute('href');
  if (!href) {
    return '';
  }

  const referencedElement = document.querySelector(href) as SVGGraphicsElement;
  if (!referencedElement) {
    return '';
  }

  const computedTransform = window
    .getComputedStyle(referencedElement)
    .getPropertyValue('transform');

  if (computedTransform === 'none' || !computedTransform) {
    return '';
  }

  const transformMatrix = new DOMMatrix(computedTransform);
  const inverseMatrix = transformMatrix; // Compute inverse transformation
  const inverseTransform = `matrix(${inverseMatrix.a}, ${inverseMatrix.b}, ${inverseMatrix.c}, ${inverseMatrix.d}, ${inverseMatrix.e}, ${inverseMatrix.f})`;

  return inverseTransform;
}

function getCenteringTransform(
  parent: SVGSVGElement,
  childElement: SVGElement,
  inverseTransform: string,
): string {
  const viewBox = parent.viewBox.baseVal;
  const viewBoxWidth = viewBox.width;
  const viewBoxHeight = viewBox.height;
  const scale = childElement.getAttribute('scale');

  const childBBox = (childElement as SVGGElement).getBBox();

  const translateX = (viewBoxWidth - childBBox.width) / 2 - childBBox.x;
  const translateY = (viewBoxHeight - childBBox.height) / 2 - childBBox.y;

  let transform = `${inverseTransform} translate(${translateX}, ${translateY})`;
  if (scale) {
    transform += ` scale(${scale})`;
  }

  return transform;
}

export function getBoundingBox(svg: string) {
  const parser = new DOMParser();
  const doc = parser.parseFromString(svg, 'image/svg+xml');
  const svgElement = doc.documentElement;
  const svgViewBox = svgElement.getAttribute('viewBox');

  if (!svgViewBox) {
    throw new Error('SVG viewBox attribute is missing');
  }
  const [minX, minY, width, height] = svgViewBox.split(' ').map(Number);

  return {
    xMin: minX,
    yMin: minY,
    xMax: width,
    yMax: height,
  };
}

async function fontUrlToBase64(url: string): Promise<string> {
  try {
    const res = await fetch(`/editor/get_b64font/${url}`);
    const data = (await res.json()) as { base64: string };
    return data.base64;
  } catch (e) {
    console.error('Error fetching font as base64:', e);
    return '';
  }
}

export type CreatePngProps = {
  svgString: string;
  loadedFonts: LoadedFont[];
  width: number;
  height: number;
  deferRender?: boolean;
  ignoreAspectRatio?: boolean;
  backgroundColor?: string;
};

export async function createPng(props: CreatePngProps): Promise<string> {
  const parser = new DOMParser();
  const doc = parser.parseFromString(props.svgString, 'image/svg+xml');

  const texts = Array.from(doc.querySelectorAll('text'));
  const uniqueTexts = texts
    .map((textElement) => {
      return textElement.getAttribute('font-family');
    })
    .filter((value, index, self) => {
      return self.indexOf(value) === index && value !== 'Arial';
    });

  const defs = document.createElement('defs');
  for (const text of uniqueTexts) {
    const font = props.loadedFonts.find((font) => {
      return font.fontFamily === text;
    });
    const style = document.createElement('style');
    const b64 = font ? await fontUrlToBase64(font.url) : '';
    style.textContent = `
      @font-face {
        font-family: "${font?.fontFamily}";
        /* .. */
        src: url(${b64}) format("woff2");
      }`;
    defs.appendChild(style);
  }
  doc.documentElement.appendChild(defs);

  const svgWithEmbeddedImages = await embedExternalImages(doc);
  return new Promise((resolve, reject) => {
    const ratio =
      props.ignoreAspectRatio && props.ignoreAspectRatio === true ? 1 : getAspectRatio(doc);
    const size = calcSize(ratio, props.width, props.height);
    const canvas = document.createElement('canvas');
    canvas.width = size.width;
    canvas.height = size.height;

    const img = new Image();
    if (props.deferRender && props.deferRender === true) {
      img.onload = async () => {
        const ctx = canvas.getContext('2d');
        let frameCount = 0;
        async function renderImage() {
          frameCount += 1;
          if (frameCount < 10) {
            requestAnimationFrame(renderImage);
            return;
          }
          ctx?.drawImage(img, 0, 0, size.width, size.height);
          const pngUrl = canvas.toDataURL('image/png');
          resolve(pngUrl);
        }

        requestAnimationFrame(renderImage);
      };
    } else {
      img.onload = async () => {
        const ctx = canvas.getContext('2d');
        if (ctx) {
          if (props.backgroundColor) {
            ctx.fillStyle = props.backgroundColor;
            ctx.fillRect(0, 0, canvas.width, canvas.height);
          }
          ctx?.drawImage(img, 0, 0, size.width, size.height);
          const pngUrl = canvas.toDataURL('image/png');
          resolve(pngUrl);
        }
      };
    }

    img.onerror = (error) => {
      reject(new Error(`Failed to load SVG: ${error}`));
    };

    const encoder = new TextEncoder(); // encodes as UTF-8
    const bytes = encoder.encode(svgWithEmbeddedImages); // Uint8Array
    // @ts-expect-error bytes.toBase64() might not be supported by all browsers yet
    const b64 = bytes.toBase64 ? bytes.toBase64() : utf8ToBase64(bytes);
    img.src = 'data:image/svg+xml;base64,' + b64;
  });
}

function utf8ToBase64(bytes: Uint8Array) {
  let binary = '';
  bytes.forEach((b) => (binary += String.fromCharCode(b)));
  return btoa(binary);
}

async function embedExternalImages(doc: Document): Promise<string> {
  const images = doc.querySelectorAll('image');

  for (const image of Array.from(images)) {
    for (const attr of ['xlink:href', 'href']) {
      const href = image.getAttribute(attr);
      if (href?.startsWith('http')) {
        const dataUrl = await fetchImageAsDataUrl(href);
        image.setAttribute(attr, dataUrl);
      }
    }
  }

  return new XMLSerializer().serializeToString(doc);
}

function fetchImageAsDataUrl(url: string): Promise<string> {
  return fetch(url, { cache: 'no-cache' })
    .then((response) => response.blob())
    .then(
      (blob) =>
        new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result as string);
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        }),
    );
}

function getAspectRatio(doc: Document) {
  const svgElement = doc.documentElement;
  const viewBox = svgElement.getAttribute('viewBox');
  if (!viewBox) {
    return 1;
  }
  const [_minX, _minY, width, height] = viewBox.split(' ').map(Number);
  return width / height;
}

export function scaleSVGPath(path: string, scale: number): string {
  const numberRegex = /-?\d*\.?\d+/g;
  return path.replace(numberRegex, (match) => {
    const number = parseFloat(match);
    return (number * scale).toString();
  });
}

function calcSize(ratio: number, width: number, height: number) {
  const size = {
    height: height,
    width: width,
  };

  if (ratio < 1) {
    size.width = width * ratio;
  }

  if (ratio > 1) {
    size.height = height / ratio;
  }

  return size;
}

function extractDimensionsFromSvg(svg: string): { width: number; height: number } {
  const parser = new DOMParser();
  const doc = parser.parseFromString(`<svg>${svg}</svg>`, 'image/svg+xml');

  const widthElem = doc.querySelector('defs[dataWidth]');
  const heightElem = doc.querySelector('defs[dataHeight]');
  const width = widthElem ? parseFloat(widthElem.getAttribute('dataWidth')!) : 0;
  const height = heightElem ? parseFloat(heightElem.getAttribute('dataHeight')!) : 0;

  return { width, height };
}

export function generateSvgStringFromPart(part: Part) {
  const { width, height } = extractDimensionsFromSvg(part.svg);
  const widthPx = mm2px(Math.round(width));
  const heightPx = mm2px(Math.round(height));
  const svgPartGroup: string = getSvgPartGroup(part, false);
  const padding: number = mm2px(4);
  const viewBox = `${-padding / 2} ${-padding / 2} ${widthPx + padding} ${heightPx + padding}`;
  return `<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" viewBox="${viewBox}">${svgPartGroup}</svg>`;
}

/* Sheets Support. */
export function generateSvgStringFromParts(
  parts: Part[],
  showCutlines: boolean = false,
  showBleedlines: boolean = false,
): string {
  const padding: number = mm2px(4);
  let allSvgParts = '';
  let maxWidth = 0;
  let maxHeight = 0;

  //sort to make part.side == front is always in the back
  const sortedParts = sortParts(parts);

  for (const part of sortedParts) {
    const isBackpart =
      part.side === 'back' || part.part_type == 'back_print' || part.meta.SIDE === 'back';
    if (isBackpart) {
      /* Skipping backpart -> use generateSvgStringFromPart() for backside print */
      continue;
    }
    const { width, height } = extractDimensionsFromSvg(part.svg);
    const widthPx = mm2px(Math.round(width));
    const heightPx = mm2px(Math.round(height));
    maxWidth = Math.max(maxWidth, widthPx);
    maxHeight = Math.max(maxHeight, heightPx);
    allSvgParts += getSvgPartGroup(part, showCutlines, showBleedlines);
  }

  const viewBox = `${-padding / 2} ${-padding / 2} ${maxWidth + padding} ${maxHeight + padding}`;

  return `<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" viewBox="${viewBox}">${allSvgParts}</svg>`;
}
function sortParts(parts: Part[]): Part[] {
  const sortedParts = [...parts].sort((a, b) => {
    if (a.side === 'front' && b.side !== 'front') {
      return -1;
    }
    if (a.side !== 'front' && b.side === 'front') {
      return 1;
    }
    return 0;
  });
  return sortedParts;
}

function getSvgPartGroup(
  part: Part,
  showCutlines: boolean = false,
  showBleedlines: boolean = false,
): string {
  let transform: string = '';
  const translate: string = part['translate'] ? `transform="translate(${part['translate']})"` : '';

  if (part['translate']) {
    const [x, y] = part['translate'].split(',').map(Number);
    transform = ` translate(${x * -1},${y * -1}) `;
  }

  const scale: number = extractScale(part['matrix']);
  const bsize: number = Math.round(mm2px(part.side ? 4 : 2) / scale); // 2mm for sheet parts (subparts) and 4 mm for single die-cuts
  const maskId: string = `mask_${generateUniqueId()}`;
  const fill: string = part['background'];
  const cutlineSize: number = Math.round(mm2px(1));

  let svg = '';
  svg += `<g transform="${part['matrix']}${transform}">`;
  svg += `<defs>`;
  svg += `<mask id="${maskId}">`;
  svg += `<path d="${part['cuttingline']}" fill="white" stroke="white" stroke-width="${bsize}" stroke-linecap="square"/>`;
  svg += `</mask>`;
  svg += `</defs>`;
  svg += `<g mask="url(#${maskId})">`;
  svg += `<path d="${part['cuttingline']}" fill="${fill}" stroke="${fill}" stroke-width="${bsize}" stroke-linecap="square"/>`;
  svg += `<g ${translate}>${part['svg']}</g>`;
  svg += `</g>`;

  if (showCutlines) {
    svg += `<path d="${part['cuttingline']}" fill="none" stroke="magenta" stroke-width="${cutlineSize}" stroke-linecap="square"/>`;
  }
  if (showBleedlines && part['bleed']) {
    svg += `<path d="${part['bleed']}" fill="none" stroke="cyan" stroke-width="${cutlineSize}" stroke-linecap="square"/>`;
  }
  svg += `</g>`;
  return svg;
}

function mm2px(mm: number): number {
  return mm * 2.83465;
}

export function extractScale(matrix: string | null | undefined): number {
  let scale: number = 1;

  if (matrix && matrix.trim() !== '') {
    matrix = matrix.substring(matrix.indexOf('matrix'));

    const matches = matrix.match(/\(([^()]+)\)/);

    if (matches && matches[1]) {
      let scaleValues: string[];

      if (matches[1].includes(',')) {
        scaleValues = matches[1].split(',');
      } else {
        scaleValues = matches[1].trim().split(/\s+/);
      }

      const x = parseFloat(scaleValues[0]);
      const y = parseFloat(scaleValues[1]);

      scale = Math.sqrt(Math.pow(x, 2) + Math.pow(y, 2));
    }
  }

  return scale;
}

export function generateUniqueId(): string {
  return Math.random().toString(36).slice(2, 11);
}

export async function loadAllEditorFonts(
  url: string = '',
  cherryPickFontFamilies: string[] = [],
): Promise<LoadedFont[]> {
  try {
    const params = new URLSearchParams();
    // Append each font family separately for better structure
    cherryPickFontFamilies.forEach((fontFamily) => params.append('fontFamilies[]', fontFamily));
    const resp = await fetch(`${url}/editor/get_fonts?${params.toString()}`);
    const fonts = (await resp.json()) as LoadedFont[];
    return fonts; // Ensures only valid arrays are returned
  } catch (_e) {
    return []; // Returns an empty array instead of throwing an error
  }
}

export function extractFontFamiliesInSVG(svgString: string): string[] {
  // Create a temporary DOM parser
  const parser = new DOMParser();
  const svgDoc = parser.parseFromString(svgString, 'image/svg+xml');

  // Get all elements that may contain font-family attributes
  const textElements = svgDoc.querySelectorAll('text, tspan');

  // Extract font families
  const fontFamilies = new Set();
  textElements.forEach((element) => {
    const fontFamily = element.getAttribute('font-family');
    if (fontFamily) {
      fontFamilies.add(fontFamily);
    }
  });

  return Array.from(fontFamilies) as string[];
}

export async function fetchFontsFromSVG(
  svgString: string,
  url: string = '',
): Promise<LoadedFont[]> {
  const fontFamilies = extractFontFamiliesInSVG(svgString);
  if (fontFamilies.length === 0) {
    return [];
  }
  return await loadAllEditorFonts(url, fontFamilies);
}
